import React, { useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Lock, ShieldCheck, MapPin, Calendar as CalendarIcon, Clock, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const WHATSAPP_NUMBER = "351920248910";

function pickupWindow(d) {
  const wd = d.getDay();
  if (wd === 0) return ["08:00", "12:00"];
  if (wd === 6) return ["08:00", "14:00"];
  return ["09:00", "19:00"];
}

function buildSlots(open, close) {
  const slots = [];
  const [oh, om] = open.split(":").map(Number);
  const [ch, cm] = close.split(":").map(Number);
  let h = oh, m = om;
  while (h < ch || (h === ch && m <= cm)) {
    slots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    m += 30;
    if (m >= 60) { m = 0; h += 1; }
  }
  return slots;
}

function minPickupDate() {
  const d = new Date();
  d.setDate(d.getDate() + 2);
  return d.toISOString().slice(0, 10);
}

const DAY_LABELS = ["domingo", "segunda-feira", "terça-feira", "quarta-feira", "quinta-feira", "sexta-feira", "sábado"];

function buildWhatsAppMessage(order) {
  const lines = [];
  lines.push(`🍰 *Nova encomenda Caseirinhos*`);
  lines.push(``);
  lines.push(`*Cliente:* ${order.customer_name}`);
  lines.push(`*Email:* ${order.customer_email}`);
  lines.push(`*Telemóvel:* ${order.customer_phone}`);
  if (order.delivery_address) lines.push(`*Morada:* ${order.delivery_address}`);
  lines.push(``);
  lines.push(`*Levantamento:* ${order.pickup_date} às ${order.pickup_time}`);
  lines.push(``);
  lines.push(`*Itens:*`);
  order.items.forEach((it) => {
    lines.push(`• ${it.name} — ${it.variant_label} × ${it.quantity} (€${(it.price * it.quantity).toFixed(2)})`);
    if (it.selected_flavors) {
      Object.entries(it.selected_flavors).forEach(([k, f]) => {
        if (f.length) lines.push(`   ${k}: ${f.join(", ")}`);
      });
    }
    if (it.customization && (it.customization.theme || it.customization.colors)) {
      if (it.customization.theme) lines.push(`   Tema: ${it.customization.theme}`);
      if (it.customization.colors) lines.push(`   Cores: ${it.customization.colors}`);
    }
  });
  lines.push(``);
  lines.push(`*Total: €${order.total.toFixed(2)}*`);
  lines.push(`Sinal 50%: €${(order.deposit_amount || 0).toFixed(2)}`);
  lines.push(`No levantamento: €${(order.remaining_amount || 0).toFixed(2)}`);
  if (order.notes) {
    lines.push(``);
    lines.push(`*Observações:* ${order.notes}`);
  }
  lines.push(``);
  lines.push(`Nº encomenda: ${order.id}`);
  return lines.join("\n");
}

export default function CheckoutPage() {
  const { items, subtotal, surchargeTotal, baseSubtotal, clear } = useCart();
  const navigate = useNavigate();
  const minDate = useMemo(() => minPickupDate(), []);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    delivery_address: "",
    notes: "",
    pickup_date: minDate,
    pickup_time: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const pickupDateObj = useMemo(() => form.pickup_date ? new Date(form.pickup_date + "T12:00:00") : null, [form.pickup_date]);
  const window_ = useMemo(() => pickupDateObj ? pickupWindow(pickupDateObj) : null, [pickupDateObj]);
  const slots = useMemo(() => window_ ? buildSlots(window_[0], window_[1]) : [], [window_]);
  const dayLabel = pickupDateObj ? DAY_LABELS[pickupDateObj.getDay()] : "";

  const deposit = Math.round(subtotal * 50) / 100;
  const remaining = Math.round((subtotal - deposit) * 100) / 100;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast.error("O cesto está vazio."); return; }
    if (!form.customer_name || !form.customer_email || !form.customer_phone) {
      toast.error("Preencha todos os campos obrigatórios."); return;
    }
    if (!form.pickup_date || !form.pickup_time) {
      toast.error("Escolha a data e hora de levantamento."); return;
    }
    if (form.pickup_date < minDate) {
      toast.error("Encomendas com antecedência mínima de 2 dias."); return;
    }
    setSubmitting(true);
    try {
      const orderItems = items.map((i) => ({
        product_id: i.product_id, name: i.name, variant_id: i.variant_id, variant_label: i.variant_label,
        price: i.price, quantity: i.quantity, category: i.category,
        selected_flavors: i.selected_flavors || {}, customization: i.customization || null,
      }));
      const { data: order } = await api.post("/orders", { ...form, items: orderItems });
      // Build WhatsApp message + open
      const msg = buildWhatsAppMessage(order);
      const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
      clear();
      window.open(url, "_blank");
      navigate(`/encomenda/${order.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar encomenda.");
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div data-testid="checkout-empty" className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h1 className="font-heading text-4xl text-cocoa-dark">O seu cesto está vazio</h1>
        <p className="text-cocoa-soft mt-3">Adicione produtos antes de finalizar a encomenda.</p>
        <Link to="/catalogo" className="inline-block mt-6 bg-rose hover:bg-rose-hover text-cocoa-dark px-7 py-3 rounded-full">Ver catálogo</Link>
      </div>
    );
  }

  const input = "w-full bg-cream border border-cocoa/15 rounded-xl px-4 py-3 text-cocoa-dark placeholder:text-cocoa-soft/60 focus:outline-none focus:ring-2 focus:ring-rose focus:border-rose transition";

  return (
    <div data-testid="checkout-page" className="max-w-7xl mx-auto px-6 sm:px-8 py-12 grid lg:grid-cols-3 gap-10">
      <form onSubmit={submit} className="lg:col-span-2 space-y-10">
        <div>
          <p className="text-xs uppercase tracking-widest text-rose-accent">Finalizar encomenda</p>
          <h1 className="font-heading text-5xl text-cocoa-dark mt-2">Quase a saborear</h1>
        </div>

        <section>
          <h2 className="font-heading text-2xl text-cocoa-dark mb-4">Os seus dados</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="input-name" name="customer_name" value={form.customer_name} onChange={handleChange} placeholder="Nome completo *" className={input} />
            <input data-testid="input-email" name="customer_email" type="email" value={form.customer_email} onChange={handleChange} placeholder="Email *" className={input} />
            <input data-testid="input-phone" name="customer_phone" value={form.customer_phone} onChange={handleChange} placeholder="Telemóvel *" className={input} />
            <input data-testid="input-address" name="delivery_address" value={form.delivery_address} onChange={handleChange} placeholder="Morada (opcional)" className={input} />
          </div>
          <textarea data-testid="input-notes" name="notes" value={form.notes} onChange={handleChange} placeholder="Observações (decoração, alergias, dedicatória...)" rows={3} className={`${input} mt-4`} />
        </section>

        <section className="bg-cream-deep border border-cocoa/10 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-rose/40 rounded-full flex items-center justify-center"><CalendarIcon className="w-5 h-5 text-cocoa-dark" /></div>
            <div>
              <h2 className="font-heading text-2xl text-cocoa-dark leading-none">Data e hora de levantamento</h2>
              <p className="text-xs uppercase tracking-widest text-cocoa-soft mt-1">Antecedência mínima · 2 dias</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-cocoa-soft mb-1.5 block">Dia *</label>
              <input data-testid="pickup-date" type="date" name="pickup_date" min={minDate} value={form.pickup_date} onChange={(e) => setForm({ ...form, pickup_date: e.target.value, pickup_time: "" })} className={input} />
              {dayLabel && <p className="text-xs text-cocoa-soft mt-1.5 capitalize">{dayLabel} · levantamento {window_?.[0]}–{window_?.[1]}</p>}
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-cocoa-soft mb-1.5 block">Hora *</label>
              <select data-testid="pickup-time" name="pickup_time" value={form.pickup_time} onChange={handleChange} className={input}>
                <option value="">Escolha um horário</option>
                {slots.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-4 flex items-start gap-2 text-xs text-cocoa-soft">
            <MapPin className="w-4 h-4 text-rose-accent shrink-0 mt-0.5" />
            <p>Levantamento em <strong className="text-cocoa-dark">Rua António Duarte Caneças nº 1, 2700-069 Amadora</strong>.</p>
          </div>
        </section>

        <section className="bg-mint/15 border border-mint/40 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-mint rounded-full flex items-center justify-center"><MessageCircle className="w-5 h-5 text-cocoa-dark" /></div>
            <div>
              <h2 className="font-heading text-2xl text-cocoa-dark leading-none">Finalizar via WhatsApp</h2>
              <p className="text-xs uppercase tracking-widest text-cocoa-soft mt-1">Combinamos consigo a forma de pagamento</p>
            </div>
          </div>
          <p className="text-sm text-cocoa-dark leading-relaxed">
            Ao confirmar, abrimos automaticamente uma conversa no WhatsApp para <strong>+351 920 248 910</strong> com o resumo completo da sua encomenda. Combinamos consigo a forma de pagamento do sinal de 50% para confirmar a encomenda.
          </p>
          <p className="text-xs text-cocoa-soft mt-3 flex items-center gap-1.5">
            <Lock className="w-3 h-3" /> A encomenda só é confirmada após o sinal ser pago.
          </p>
        </section>

        <button
          data-testid="place-order-button"
          disabled={submitting}
          type="submit"
          className="w-full bg-cocoa-dark hover:bg-cocoa text-cream py-4 rounded-full font-medium transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
        >
          <MessageCircle className="w-4 h-4" />
          {submitting ? "A criar encomenda..." : "Confirmar e enviar via WhatsApp"}
        </button>
      </form>

      <aside className="bg-cream-deep rounded-2xl p-6 h-fit lg:sticky lg:top-28 border border-cocoa/10">
        <h3 className="font-heading text-2xl text-cocoa-dark mb-4">Resumo</h3>
        <ul className="divide-y divide-cocoa/10">
          {items.map((it) => (
            <li key={it.line_key} className="py-3 flex gap-3 text-sm">
              <img src={it.image_url} className="w-14 h-14 rounded-lg object-cover" alt="" />
              <div className="flex-1">
                <p className="font-medium text-cocoa-dark leading-tight">{it.name}</p>
                <p className="text-xs text-cocoa-soft">{it.variant_label} × {it.quantity}</p>
                {it.selected_flavors && Object.entries(it.selected_flavors).map(([k, f]) => f.length > 0 && (
                  <p key={k} className="text-[11px] text-cocoa-soft/80 leading-snug mt-0.5"><span className="capitalize">{k}:</span> {f.join(", ")}</p>
                ))}
                {it.customization && (it.customization.theme || it.customization.colors) && (
                  <div className="text-[11px] text-cocoa-soft mt-0.5">
                    {it.customization.theme && <p>Tema: <span className="text-cocoa-dark">{it.customization.theme}</span></p>}
                    {it.customization.colors && <p>Cores: <span className="text-cocoa-dark">{it.customization.colors}</span></p>}
                  </div>
                )}
              </div>
              <span className="text-cocoa-dark">€{(it.price * it.quantity).toFixed(2)}</span>
            </li>
          ))}
        </ul>
        {surchargeTotal > 0 && (
          <div className="border-t border-cocoa/10 mt-2 pt-3 space-y-1">
            <div className="flex justify-between text-xs text-cocoa-soft"><span>Subtotal</span><span>€{baseSubtotal.toFixed(2)}</span></div>
            <div className="flex justify-between text-xs text-rose-accent"><span>Sabores premium</span><span>+€{surchargeTotal.toFixed(2)}</span></div>
          </div>
        )}
        <div className="border-t border-cocoa/10 mt-3 pt-4 space-y-1">
          <div className="flex justify-between items-baseline">
            <span className="text-xs uppercase tracking-widest text-cocoa-soft">Total</span>
            <span data-testid="checkout-total" className="font-heading text-3xl text-cocoa-dark">€{subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm text-cocoa-soft mt-2"><span>Sinal (50%)</span><span className="text-cocoa-dark">€{deposit.toFixed(2)}</span></div>
          <div className="flex justify-between text-sm text-cocoa-soft"><span>No levantamento</span><span className="text-cocoa-dark">€{remaining.toFixed(2)}</span></div>
        </div>
        <p className="text-xs text-cocoa-soft mt-5 flex items-center gap-1.5">
          <ShieldCheck className="w-3 h-3 text-mint" /> Resumo enviado por WhatsApp.
        </p>
        <p className="text-xs text-cocoa-soft mt-3 flex items-center gap-1.5">
          <Clock className="w-3 h-3 text-rose-accent" /> Pedidos preparados em pequenos lotes diários.
        </p>
      </aside>
    </div>
  );
}
